import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import Cropper from 'react-easy-crop';
import getCroppedImg from './CropImage';
import './css/UploadDiskImage.css';
import { useImageContext } from '../contexts/ImageContext';
import { ensureBrowserCompatibleImage, getImageInputAccept } from '../utils/convertHeic';
import { useToast } from '../contexts/ToastContext';

const convertToCircularImage = async (croppedImage) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        const handleImageLoad = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const size = Math.min(img.width, img.height);
            canvas.width = size;
            canvas.height = size;

            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            ctx.beginPath();
            ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
            ctx.clip();
            ctx.drawImage(img, (size - img.width) / 2, (size - img.height) / 2);

            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(URL.createObjectURL(blob));
                } else {
                    reject(new Error('Canvas to Blob conversion failed'));
                }
            }, 'image/png', 1.0);
        };

        img.onload = handleImageLoad;
        img.onerror = reject;

        if (croppedImage instanceof Blob) {
            img.src = URL.createObjectURL(croppedImage);
        } else if (typeof croppedImage === 'string') {
            fetch(croppedImage)
                .then(res => res.blob())
                .then(blob => {
                    img.src = URL.createObjectURL(blob);
                })
                .catch(reject);
        } else {
            reject(new Error('Invalid image format'));
        }
    });
};

const commitCroppedImage = async (imageSrc, pixels, setCurrentImage) => {
    if (!imageSrc || !pixels) {
        return;
    }
    try {
        const croppedImageBlob = await getCroppedImg(imageSrc, pixels);
        const circularImage = await convertToCircularImage(croppedImageBlob);
        setCurrentImage(circularImage);
    } catch (e) {
        // Silent error handling
    }
};

const DiskImage = ({ imageName }) => {
    const { setCurrentImage, uploadedImage, setUploadedImage, setImageName, crop, setCrop, zoom, setZoom } = useImageContext();
    const toast = useToast();
    const [isProcessingUpload, setIsProcessingUpload] = useState(false);

    const handleMenuToggle = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        window.dispatchEvent(new CustomEvent('toggleNavigationMenu'));
    };
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [cropSize, setCropSize] = useState({ width: 0, height: 0 });
    const [minZoom, setMinZoom] = useState(1);
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [mediaSize, setMediaSize] = useState({ width: 0, height: 0 });
    const cropContainerRef = useRef(null);
    const isInteractingRef = useRef(false);
    const cropUpdateTimeoutRef = useRef(null);
    const zoomUpdateTimeoutRef = useRef(null);
    // Latest crop must survive debounce / navigation races (esp. mobile Next right after pan)
    const croppedAreaPixelsRef = useRef(null);
    const uploadedImageRef = useRef(uploadedImage);
    uploadedImageRef.current = uploadedImage;

    // Calculate minimum zoom to ensure image covers the circular crop area
    // This uses the natural size from react-easy-crop's onMediaLoaded callback
    const calculateMinZoom = useCallback((naturalWidth, naturalHeight, cropWidth, containerWidth, containerHeight) => {
        if (!naturalWidth || !naturalHeight || !cropWidth || !containerWidth || !containerHeight) return 1;
        
        // Calculate how react-easy-crop fits the image to the container
        // It maintains aspect ratio and fits within the container
        const imgAspect = naturalWidth / naturalHeight;
        const containerAspect = containerWidth / containerHeight;
        
        let fittedWidth, fittedHeight;
        if (imgAspect > containerAspect) {
            // Image is wider - fit to container height
            fittedHeight = containerHeight;
            fittedWidth = containerHeight * imgAspect;
        } else {
            // Image is taller - fit to container width
            fittedWidth = containerWidth;
            fittedHeight = containerWidth / imgAspect;
        }
        
        // For a circular crop, we need the image to cover the diagonal of the square crop area
        // The diagonal of a square is: diagonal = cropWidth * √2
        const cropDiagonal = cropWidth * Math.SQRT2;
        
        // We need the smaller dimension of the fitted image (when zoomed) to be at least the crop diagonal
        const minFittedDimension = Math.min(fittedWidth, fittedHeight);
        const calculatedMinZoom = cropDiagonal / minFittedDimension;
        
        // Add a small buffer (5%) to ensure full coverage without gaps
        // Reduced from 20% to allow more zoom-in capability while still preventing blank areas
        return Math.max(calculatedMinZoom * 1.05, 1);
    }, []);

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            return;
        }

        setIsProcessingUpload(true);

        try {
            // HEIC/HEIF is not decodable in most browsers (or for canvas/crop).
            // Safari decodes HEIC natively; other browsers convert via heic2any.
            const compatibleFile = await ensureBrowserCompatibleImage(file);

            await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const imageUrl = reader.result;
                    const img = new Image();
                    img.onload = () => {
                        setUploadedImage(imageUrl);
                        setImageName(compatibleFile.name);
                        setImageSize({ width: img.width, height: img.height });

                        if (cropSize.width > 0 && cropContainerRef.current) {
                            const containerRect = cropContainerRef.current.getBoundingClientRect();
                            const minZoomValue = calculateMinZoom(
                                img.width,
                                img.height,
                                cropSize.width,
                                containerRect.width,
                                containerRect.height
                            );
                            setMinZoom(minZoomValue);
                            setZoom(prevZoom => Math.max(prevZoom, minZoomValue));
                            setCrop({ x: 0, y: 0 });
                        }
                        resolve();
                    };
                    img.onerror = () => {
                        reject(new Error('Image could not be loaded'));
                    };
                    img.src = imageUrl;
                };
                reader.onerror = () => {
                    reject(new Error('Could not read image file'));
                };
                reader.readAsDataURL(compatibleFile);
            });
        } catch (e) {
            toast.error('Could not load that image. Try another photo or save it as JPEG.');
        } finally {
            setIsProcessingUpload(false);
            // Allow re-selecting the same file after a failed/partial attempt
            event.target.value = '';
        }
    };

    // Throttled crop change handler to prevent excessive updates.
    // Uses its own timeout — must not clear onCropComplete / auto-crop commits.
    const handleCropChange = useCallback((newCrop) => {
        isInteractingRef.current = true;
        clearTimeout(cropUpdateTimeoutRef.current);
        setCrop(newCrop);
        cropUpdateTimeoutRef.current = setTimeout(() => {
            isInteractingRef.current = false;
        }, 150);
    }, [setCrop]);

    // Handle media loaded - this gives us the actual natural size react-easy-crop sees
    const onMediaLoaded = useCallback((mediaSize) => {
        setMediaSize(mediaSize);
        
        // Recalculate min zoom with actual media dimensions
        if (cropSize.width > 0 && cropContainerRef.current) {
            const containerRect = cropContainerRef.current.getBoundingClientRect();
            const minZoomValue = calculateMinZoom(
                mediaSize.naturalWidth,
                mediaSize.naturalHeight,
                cropSize.width,
                containerRect.width,
                containerRect.height
            );
            setMinZoom(minZoomValue);
            // Only enforce minimum if current zoom is below it - don't force zoom up
            // This allows users to zoom in more if they want
            setZoom(prevZoom => {
                if (prevZoom < minZoomValue) {
                    return minZoomValue;
                }
                return prevZoom;
            });
        }
    }, [cropSize, calculateMinZoom, setZoom]);

    // Throttled zoom change handler with strict minimum zoom enforcement
    const handleZoomChange = useCallback((newZoom) => {
        // Strictly enforce minimum zoom - reject any zoom below minimum immediately
        if (newZoom < minZoom && minZoom > 0) {
            // Don't update zoom if it's below minimum - this prevents the visual change
            setZoom(minZoom);
            return;
        }
        
        isInteractingRef.current = true;
        clearTimeout(zoomUpdateTimeoutRef.current);
        // Ensure zoom is at least minimum (safety check)
        const clampedZoom = Math.max(newZoom, minZoom);
        setZoom(clampedZoom);
        zoomUpdateTimeoutRef.current = setTimeout(() => {
            isInteractingRef.current = false;
        }, 150); // Mark as not interacting after 150ms of no updates
    }, [setZoom, minZoom]);

    // react-easy-crop calls this on interaction end (mouse up / touch end).
    // Commit pixels immediately so a quick mobile Next doesn't race a debounce.
    const onCropComplete = useCallback((_croppedArea, pixels) => {
        croppedAreaPixelsRef.current = pixels;
        setZoom(prevZoom => Math.max(prevZoom, minZoom));
        setCroppedAreaPixels(pixels);
        isInteractingRef.current = false;
    }, [minZoom, setZoom]);

    const handleAutoCrop = useCallback(async () => {
        if (!croppedAreaPixels) {
            return;
        }
        await commitCroppedImage(uploadedImage, croppedAreaPixels, setCurrentImage);
    }, [uploadedImage, croppedAreaPixels, setCurrentImage]);

    useEffect(() => {
        let resizeTimeout;
        let resizeObserver;
        let rafId;
        
        const updateCropSize = () => {
            if (cropContainerRef.current && !isInteractingRef.current) {
                const { width, height } = cropContainerRef.current.getBoundingClientRect();
                const size = Math.min(width, height) * 0.8; // 80% of the smaller dimension for square crop
                setCropSize(prev => {
                    // Only update if size actually changed significantly (more than 5px)
                    if (Math.abs(prev.width - size) > 5 || Math.abs(prev.height - size) > 5) {
                        const newSize = { width: size, height: size };
                        
                        // Recalculate min zoom when crop size changes
                        if (mediaSize.width > 0 && mediaSize.height > 0 && cropContainerRef.current) {
                            const containerRect = cropContainerRef.current.getBoundingClientRect();
                            const minZoomValue = calculateMinZoom(
                                mediaSize.naturalWidth || mediaSize.width, 
                                mediaSize.naturalHeight || mediaSize.height, 
                                size,
                                containerRect.width,
                                containerRect.height
                            );
                            setMinZoom(minZoomValue);
                            // Ensure current zoom is at least the minimum
                            setZoom(prevZoom => Math.max(prevZoom, minZoomValue));
                        }
                        
                        return newSize;
                    }
                    return prev;
                });
            }
        };

        // Debounced resize handler with requestAnimationFrame for smoother updates
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                if (rafId) cancelAnimationFrame(rafId);
                rafId = requestAnimationFrame(() => {
                    updateCropSize();
                });
            }, 300); // Increased debounce time
        };

        // Initial sizing
        updateCropSize();

        // Use ResizeObserver but only update when not interacting
        if (cropContainerRef.current && window.ResizeObserver) {
            resizeObserver = new ResizeObserver((entries) => {
                // Only update if not currently interacting
                if (!isInteractingRef.current) {
                    handleResize();
                }
            });
            resizeObserver.observe(cropContainerRef.current);
        }

        // Fallback to window resize for older browsers
        window.addEventListener('resize', handleResize, { passive: true });

        // Capture ref value for cleanup
        const currentCropContainer = cropContainerRef.current;

        return () => {
            clearTimeout(resizeTimeout);
            if (rafId) cancelAnimationFrame(rafId);
            window.removeEventListener('resize', handleResize);
            if (resizeObserver && currentCropContainer) {
                resizeObserver.unobserve(currentCropContainer);
            }
        };
    }, [imageSize, calculateMinZoom, setZoom, mediaSize]);

    // Recalculate min zoom when media size or crop size changes
    useEffect(() => {
        if (mediaSize.width > 0 && mediaSize.height > 0 && cropSize.width > 0 && cropContainerRef.current) {
            const containerRect = cropContainerRef.current.getBoundingClientRect();
            const minZoomValue = calculateMinZoom(
                mediaSize.naturalWidth || mediaSize.width, 
                mediaSize.naturalHeight || mediaSize.height, 
                cropSize.width,
                containerRect.width,
                containerRect.height
            );
            setMinZoom(minZoomValue);
            // Strictly enforce minimum zoom - immediately correct if below
            setZoom(prevZoom => Math.max(prevZoom, minZoomValue));
        }
    }, [mediaSize, cropSize, calculateMinZoom, setZoom]);

    // Watch zoom value and enforce minimum in real-time
    useEffect(() => {
        if (zoom < minZoom && minZoom > 0) {
            // Immediately correct if zoom somehow goes below minimum
            setZoom(minZoom);
        }
    }, [zoom, minZoom, setZoom]);

    // Debounce canvas export only — pixels are already committed in onCropComplete.
    // Do not gate on isInteractingRef: refs don't re-trigger effects, which skipped
    // exports after touch-end when a late onCropChange left interacting=true.
    useEffect(() => {
        if (!uploadedImage || !croppedAreaPixels) {
            return;
        }
        const timeoutId = setTimeout(() => {
            handleAutoCrop();
        }, 150);
        return () => clearTimeout(timeoutId);
    }, [uploadedImage, croppedAreaPixels, handleAutoCrop]);

    // Flush latest crop into ImageContext before unmount (e.g. mobile Next to Colours)
    // so Colors/DiskPreview never read a stale currentImage.
    useEffect(() => {
        return () => {
            clearTimeout(cropUpdateTimeoutRef.current);
            clearTimeout(zoomUpdateTimeoutRef.current);
            const pixels = croppedAreaPixelsRef.current;
            const image = uploadedImageRef.current;
            if (pixels && image) {
                commitCroppedImage(image, pixels, setCurrentImage);
            }
        };
    }, [setCurrentImage]);

    return (
        <div className="disk-image-container">
            <div className="left-column">
                <div className='upload-image-container'>
                    <div className="page-header-with-menu">
                        <button
                            className="page-header-menu-button"
                            onClick={handleMenuToggle}
                            onTouchEnd={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleMenuToggle(e);
                            }}
                            type="button"
                            aria-label="Open menu"
                        >
                            <FontAwesomeIcon icon={faBars} />
                        </button>
                        <h2 className="page-title-mobile">Image</h2>
                        <h2 className="page-title-desktop">Image</h2>
                    </div>
                    <p className="page-description">
                        Upload your artwork or image to use as the centerpiece of your render.
                    </p>
                    <label htmlFor="image-upload" className="image-file-upload">
                        {isProcessingUpload ? 'Processing…' : 'Choose Image'}
                    </label>
                    <input
                        id="image-upload"
                        type="file"
                        className="file-input"
                        onChange={handleImageUpload}
                        accept={getImageInputAccept()}
                        disabled={isProcessingUpload}
                    />
                </div>
            </div>
            <div className='right-column'>
                <div className="crop-container" ref={cropContainerRef}>
                    {uploadedImage && cropSize.width > 0 && (
                        <Cropper
                            image={uploadedImage}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            cropSize={cropSize}
                            minZoom={minZoom}
                            maxZoom={5}
                            onCropChange={handleCropChange}
                            onZoomChange={handleZoomChange}
                            onCropComplete={onCropComplete}
                            onMediaLoaded={onMediaLoaded}
                            restrictPosition={true}
                            showGrid={false}
                            style={{
                                containerStyle: {
                                    position: 'relative',
                                    width: '100%',
                                    height: '100%',
                                },
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default DiskImage;
